import { datadogRum } from "@datadog/browser-rum"
import { Platform, Policy as PlatformPolicy } from "@shared/platform-api/generated"
import { createRequest, platform, createAxiosRetryConfig } from "@shared/platform-api/requests"
import { localDateTimeFromString } from "@shared/utils/formatters/dates"
import { UUID } from "io-ts-types/lib/UUID"
import { LocalDateTime } from "@flockcover/flock-svc-core-types"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { createRequestHook } from "../createRequestHook"
import { useApi } from "../../contexts/hooks/useApi"
import { Policy, PolicyStatus, makePolicy } from "./types"
import { AuthUserType } from "../../auth/shared/types"
import { sortByDescendingExpiry } from "./helpers"

export type UseFindPolicyInput = Readonly<{ policyId?: string; userId?: string; authType?: AuthUserType | null }>

export type PrevPolicyDetails = {
  id: UUID
  startTime: LocalDateTime
  endTime: LocalDateTime
  userId?: string
  isActive: boolean
  policyNumber: string
}

type FindPolicyOutput = {
  policy: Policy
  prevPolicies: PrevPolicyDetails[] | null
  policyId?: string
}

type Output = FindPolicyOutput | null

const findCustomerPolicy = async (api: Platform, customerId: string, policyId?: string) => {
  const res = await api.policies.searchPolicies({ customerId })
  const unvoidedPolicies = res.items.filter((policy) => !/void/.test(policy.status.tag))
  const policies = sortByDescendingExpiry(unvoidedPolicies)
  const activeCustomerPolicy = policyId ? policies.find((p) => p.id === policyId) : policies.find((p) => p.isActive)
  const customerPolicy = activeCustomerPolicy || policies[0]
  return {
    customerPolicy,
    otherPolicies: policies.filter(
      (policy) => policy.id !== customerPolicy?.id && (/renewed|bound/.test(policy.status.tag) || policy.isActive)
    ),
  }
}

const findPolicyFromId = async (api: Platform, id: string) => {
  const policy = await api.policies.findPolicy({ id })
  return policy
}

const findPolicy = createRequest<UseFindPolicyInput, Output>(
  async ({ policyId, userId, authType }: UseFindPolicyInput, getToken): Promise<Output> => {
    const retryConfig = createAxiosRetryConfig({
      onRetry: (retryCount, error) => {
        // Temporary tracking of retries
        datadogRum.addAction(`api-retry`, {
          location: `useFindPolicy`,
          retryCount,
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
        })
      },
    })
    const api = platform(getToken, retryConfig)
    let policy
    let prevPolicies

    if (userId && authType !== `distributor` && authType !== `claims_admin` && authType !== `carrier`) {
      const { customerPolicy, otherPolicies } = await findCustomerPolicy(api, userId, policyId)
      policy = customerPolicy
      prevPolicies = customerPolicy ? [customerPolicy, ...otherPolicies] : otherPolicies
    } else if (policyId) {
      policy = await findPolicyFromId(api, policyId)
    }

    const underwriter = policy?.capacityProviders[0]

    return !policy
      ? null
      : {
          policy: makePolicy({
            endTime: localDateTimeFromString(policy.expiryTime),
            id: policy.id as UUID,
            meta: policy.meta,
            policyHolder: policy.insured.name,
            policyNumber: policy.number,
            productType: policy.meta.productType,
            startTime: localDateTimeFromString(policy.inceptionTime),
            status: toStatus(policy),
            totalWrittenPremium: Object.values(policy.writtenPremium).reduce((a, b) => a + b) || 0,
            underwriter: { id: underwriter?.id, name: underwriter?.name, formalName: underwriter?.formalName },
            isActive: policy.isActive,
            coiDocumentUrl: policy.coiDocumentUrl,
            effectiveSchedule: policy.effectiveSchedule,
            connectivityRatio: policy.connectivityRatio,
            grossEarnedLossRatio: policy.grossEarnedLossRatio,
            netEarnedLossRatio: policy.netEarnedLossRatio,
            claimsFrequency: formatPercentage(policy.claimsFrequency, 1, false),
            averageCappedTimeToReport: policy.averageCappedTimeToReport,
            grossEarnedLossRatioAtDates: policy.grossEarnedLossRatioAtDates,
            fnolClaimsAdmin: policy.fnolClaimsAdmin,
          }),
          prevPolicies: !prevPolicies
            ? null
            : prevPolicies
                .sort(({ expiryTime: a }, { expiryTime: b }) => new Date(b).getTime() - new Date(a).getTime())
                .map((prevPolicy) => ({
                  id: prevPolicy.id as UUID,
                  startTime: localDateTimeFromString(prevPolicy.inceptionTime),
                  endTime: localDateTimeFromString(prevPolicy.expiryTime),
                  isActive: prevPolicy.isActive,
                  policyNumber: prevPolicy.number,
                  userId,
                }))
                .filter(({ id }) => id !== `694706f8-9411-48b8-8cf4-eb12b6f2b97a`), // TODO: Filters out a fleet-less policy (until Orchestrator is updated to handle this specific issue)
        }
  }
)

const toStatus = (policy: PlatformPolicy): PolicyStatus => {
  switch (policy.status.tag) {
    case `bound`:
      return `bound`
    case `cancelled`:
      return `cancelled`
    case `lapsed`:
      return `bound`
    case `renewed`:
      return `bound`
    case `void`:
      return `void`
    default:
      throw new Error(`Unknown Status`)
  }
}

export const useFindPolicy = (input: UseFindPolicyInput) => {
  const [res, reload] = createRequestHook(findPolicy)(input)
  return useApi<Output, UseFindPolicyInput>(res, reload)
}
